import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { Key, ArrowLeft, ShieldCheck, CheckCircle2, Lock, KeyRound } from 'lucide-react';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Get token from URL
  const token = searchParams.get('token') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || password !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setIsLoading(true);
    const response = await authService.resetPassword({ token, password });

    if (response.success) {
      showToast(response.message || 'Password reset successful', 'success');
      setIsSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } else {
      showToast(response.message || 'Failed to reset password', 'error');
    }
    setIsLoading(false);
  };

  if (isSuccess) {
    return (
      <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500 text-center">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20">
            <CheckCircle2 size={40} />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">Keys Updated</h2>
          <p className="text-[11px] text-brand-muted uppercase font-bold tracking-[0.2em] px-4 leading-relaxed">
            New security credentials have been registered with the Nexus protocol.
            Redirecting to terminal...
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-2 animate-in fade-in slide-in-from-left-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-primary/10 rounded-lg">
            <KeyRound className="text-brand-primary" size={20} />
          </div>
          <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">Rotate Credentials</h2>
        </div>
        <p className="text-[11px] text-brand-muted uppercase font-bold tracking-[0.2em] opacity-60">Provision new access keys</p>
      </div>

      <div className="space-y-5">
        <div className="animate-in fade-in slide-in-from-bottom-4 delay-100 duration-500">
          <Input
            label="New Security Key"
            placeholder="••••••••"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="animate-in fade-in slide-in-from-bottom-4 delay-200 duration-500">
          <Input
            label="Confirm New Key"
            placeholder="••••••••"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="pt-2 animate-in fade-in slide-in-from-bottom-4 delay-300 duration-500">
        <Button type="submit" className="w-full h-16" isLoading={isLoading}>
          UPDATE ACCESS PROTOCOL
        </Button>
      </div>
    </form>
  );
};

export default ResetPassword;
